import { AsyncPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faBell, faCodeBranch, faEye } from '@fortawesome/pro-solid-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import Keycloak from 'keycloak-js';
import { Checkbox, CheckboxChangeEvent } from 'primeng/checkbox';
import { Drawer } from 'primeng/drawer';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { combineLatest, map } from 'rxjs';
import { Note, NoteCollection } from 'src/app/rgs/components/objects/object-item/object-inspection/models/note.model';
import { NoteService } from 'src/app/rgs/components/objects/object-item/object-inspection/services/note.service';
import { PermissionsService } from 'src/app/shared/services/permissions.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { NotesSidebarService } from './services/notes-sidebar.service';

@Component({
  selector: 'app-notifications-sidebar',
  templateUrl: './notifications-sidebar.component.html',
  imports: [Drawer, Tag, FaIconComponent, Checkbox, Tooltip, AsyncPipe, TitleCasePipe, DatePipe, TranslatePipe],
})
export class NotificationsSidebarComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly keycloak = inject(Keycloak);
  private readonly permissionsService = inject(PermissionsService);
  private readonly noteService = inject(NoteService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notesSidebarService = inject(NotesSidebarService);

  public readonly faBell = faBell;

  public readonly notificationsSidebarVisible = signal(false);
  public notes$ = this.notesSidebarService.items$.pipe(
    map((notes) =>
      notes.filter(
        (note) =>
          !ArrayUtils.ensureArray(note?.gelezen)
            .map((read) => read.gebruiker)
            .includes(this.userName),
      ),
    ),
  );

  public notesAnimationState = new Map<string, boolean>();

  public ngOnInit(): void {
    this.loadData();
    this.handleRemoveOrAddNoteIfRead();
    this.setInitialStateNotesAnimationState();
  }

  public loadData() {
    this.notesSidebarService.findUnreadNotes();
  }

  public setInitialStateNotesAnimationState() {
    this.notes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((notes) => {
      notes.forEach((note) => {
        this.notesAnimationState.set(note.id, false);
      });
    });
  }

  public onNoteMouseEnter(note: Note) {
    this.notesAnimationState.set(note.id, true);
  }

  public onNoteMouseLeave(note: Note) {
    this.notesAnimationState.set(note.id, false);
  }

  public handleRemoveOrAddNoteIfRead() {
    combineLatest([this.notesSidebarService.items$, this.noteService.items$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([sidebarNotes, singleChatNotes]) => {
        sidebarNotes.forEach((sidebarNote) => {
          const singleChatNote = singleChatNotes.find((singleChatNote) => singleChatNote.id === sidebarNote.id);

          if (singleChatNote && JSON.stringify(singleChatNote?.gelezen) !== JSON.stringify(sidebarNote?.gelezen)) {
            this.notesSidebarService.updateItemsValues([
              ...sidebarNotes.filter((item) => item.id !== singleChatNote.id),
              singleChatNote,
            ]);
          }
        });
      });
  }

  public showDayDivider(previousNote: Note, actualNote: Note) {
    if (!previousNote) {
      return true;
    }
    if (previousNote?._metadata?.created && actualNote?._metadata?.created) {
      const previousDate = new Date(previousNote?._metadata?.created);
      const actualDate = new Date(actualNote?._metadata?.created);

      if (
        previousDate?.getDay() !== actualDate?.getDay() ||
        previousDate?.getMonth() !== actualDate?.getMonth() ||
        previousDate?.getFullYear() !== actualDate?.getFullYear()
      ) {
        return true;
      }
      return false;
    }
    return true;
  }

  public goTo(note: Note) {
    switch (note.collectie) {
      case NoteCollection.INSPECTION:
        this.router.navigate(
          ['objects', note.rgsObjectId, 'inspections', 'chat', NoteCollection.INSPECTION, note.naam],
          {
            fragment: note.id,
          },
        );
        break;
      case NoteCollection.SCENARIO:
        this.router.navigate(['objects', note.rgsObjectId, 'scenario', 'chat', NoteCollection.SCENARIO, note.naam], {
          fragment: note.id,
        });
        break;
    }
  }

  public getIconByCollection(collection: NoteCollection) {
    switch (collection) {
      case NoteCollection.INSPECTION:
        return faEye;
      case NoteCollection.SCENARIO:
        return faCodeBranch;
    }
  }

  public markAsRead(event: CheckboxChangeEvent, note: Note) {
    const readUpdated = [...note.gelezen];
    const index = readUpdated.findIndex((read) => read.gebruiker === this.userName);

    if (index >= 0) {
      if (!event.checked) {
        readUpdated.splice(index, 1);
      }
    } else if (event.checked) {
      readUpdated.push({
        bedrijf: this.license,
        gebruiker: this.userName,
        tijdstip: new Date(),
      });
    }

    let noteUpdated: Record<string, unknown> = {
      ...note,
      gelezen: readUpdated,
    };
    if (!('_links' in (note as unknown as Record<string, unknown>))) {
      const baseSelfUrl = (note as unknown as { links: { href: string }[] })?.links[0]?.href;

      noteUpdated = {
        ...noteUpdated,
        _links: {
          note: {
            href: `${baseSelfUrl}/note/${note.id}`,
          },
          self: {
            href: `${baseSelfUrl}/note/${note.id}`,
          },
        },
      };
    }

    this.notesSidebarService.updateItem(noteUpdated as unknown as Note);
  }

  public get license() {
    return this.permissionsService.license as string;
  }

  public get userName() {
    return this.keycloak.idTokenParsed?.['name'];
  }
}
